/**
 * Deletion utilities for canvas operations
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Convert off-canvas image objects so blob URLs become data URLs (using the live .image element).
 * This ensures undo can load the image after JSON serialization (blob URLs are not loadable from history).
 * Strips .image, .originalImage, .compressedImage so the saved state triggers load-from-URL on restore.
 * @param {Array} images - Array of off-canvas image objects (may have .image and blob imageDataUrl/src)
 * @returns {Array} New array safe for history (data URLs, no HTMLImageElement refs)
 */
function offCanvasImagesForHistory(images) {
  if (!Array.isArray(images) || images.length === 0) return images;
  return images.map((img) => {
    const hasBlob =
      (img.imageDataUrl && img.imageDataUrl.startsWith("blob:")) ||
      (img.src && img.src.startsWith("blob:"));
    const hasImage =
      img.image &&
      typeof img.image === "object" &&
      img.image.nodeName === "IMG";
    if (hasBlob && hasImage) {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.image.naturalWidth || img.image.width || 100;
        canvas.height = img.image.naturalHeight || img.image.height || 100;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img.image, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          const { image, originalImage, compressedImage, ...rest } = img;
          return { ...rest, imageDataUrl: dataUrl, src: dataUrl };
        }
      } catch (e) {
        // Fallback: keep object but strip non-serializable refs so restore still tries to load
        const { image, originalImage, compressedImage, ...rest } = img;
        return rest;
      }
    }
    const { image, originalImage, compressedImage, ...rest } = img;
    return { ...rest };
  });
}

/**
 * Create a function to delete the selected object
 * @param {Object} params - Parameters for deletion
 * @param {Object} params.selectedObject - Currently selected object
 * @param {boolean} params.isElementBeingEdited - Whether element is being edited
 * @param {Function} params.endElementEditing - Function to end element editing
 * @param {Function} params.setAction - Setter for action state
 * @param {string} params.selectAction - Select action constant (e.g., "SELECT")
 * @param {Function} params.findAndRemoveFromState - Function to find and remove from state
 * @param {Object} params.mainStates - Main canvas state arrays and setters
 * @param {Object} params.offCanvasStates - Off-canvas state arrays and setters
 * @param {Function} params.setSelectedText - Setter for selected text
 * @param {Function} params.setSelectedTextId - Setter for selected text ID
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.setSelectedObjects - Setter for selected objects array
 * @param {React.RefObject} params.transformerRef - Reference to transformer
 * @param {Function} params.saveToHistory - Function to save to history
 * @param {Function} params.setPendingChanges - Function to set pending changes flag
 * @param {React.RefObject} params.pendingChangesRef - Ref to track pending changes state
 * @returns {Function} Function to delete selected object
 */
export const createDeleteSelectedObject = ({
  selectedObject,
  isElementBeingEdited,
  endElementEditing,
  setAction,
  selectAction,
  findAndRemoveFromState,
  mainStates,
  offCanvasStates,
  setSelectedText,
  setSelectedTextId,
  setSelectedObject,
  setSelectedObjects,
  transformerRef,
  saveToHistory,
  setPendingChanges,
  pendingChangesRef,
}) => {
  return () => {
    setAction(selectAction);

    if (!selectedObject) {
      return;
    }

    // CRITICAL: Clear pendingChanges before calling endElementEditing
    // When deleting, we don't want endElementEditing to save an element_edit_complete entry
    // The delete operation will save its own delete_object entry
    if (isElementBeingEdited) {
      // Clear pending changes so endElementEditing doesn't save
      setPendingChanges(false);
      if (pendingChangesRef) {
        pendingChangesRef.current = false;
      }
      endElementEditing();
    }

    // CRITICAL: Save the current state BEFORE deletion to history
    // This ensures undo can restore the state before the element was deleted
    // We need to save the state as it is NOW (before deletion), not after
    // IMPORTANT: Create deep copies to prevent mutation when we filter arrays later
    const stateBeforeDeletion = {
      rectangles: JSON.parse(JSON.stringify(mainStates.rectangles || [])),
      circles: JSON.parse(JSON.stringify(mainStates.circles || [])),
      arrows: JSON.parse(JSON.stringify(mainStates.arrows || [])),
      doubleSidedArrows: JSON.parse(
        JSON.stringify(mainStates.doubleSidedArrows || [])
      ),
      scribbles: JSON.parse(JSON.stringify(mainStates.scribbles || [])),
      texts: JSON.parse(JSON.stringify(mainStates.texts || [])),
      images: JSON.parse(JSON.stringify(mainStates.images || [])),
      videos: JSON.parse(JSON.stringify(mainStates.videos || [])),
      lines: JSON.parse(JSON.stringify(mainStates.lines || [])),
    };

    // CRITICAL: Manually filter arrays to capture updated state after deletion
    // Compute the filtered arrays that represent the state AFTER deletion
    let updatedRectangles = mainStates.rectangles.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedCircles = mainStates.circles.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedArrows = mainStates.arrows.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedDoubleSidedArrows = mainStates.doubleSidedArrows.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedScribbles = mainStates.scribbles.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedTexts = mainStates.texts.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedImages = mainStates.images.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedVideos = mainStates.videos.filter(
      (item) => item.id !== selectedObject.id
    );
    let updatedLines = mainStates.lines.filter(
      (item) => item.id !== selectedObject.id
    );

    // Try to delete from main canvas states first
    let deletedFromMain = false;

    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.rectangles.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedRectangles = mainStates.rectangles.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setRectangles((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedRectangles((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.circles.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedCircles = mainStates.circles.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setCircles((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedCircles((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.arrows.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedArrows = mainStates.arrows.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setArrows((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedArrows((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.doubleSidedArrows.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedDoubleSidedArrows = mainStates.doubleSidedArrows.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setDoubleSidedArrows((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedDoubleSidedArrows((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.scribbles.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedScribbles = mainStates.scribbles.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setScribbles((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedScribbles((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.texts.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedTexts = mainStates.texts.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setTexts((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedTexts((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.images.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedImages = mainStates.images.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setImages((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedImages((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.videos.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedVideos = mainStates.videos.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setVideos((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedVideos((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();
    deletedFromMain =
      deletedFromMain ||
      (() => {
        const found = mainStates.lines.find(
          (item) => item.id === selectedObject.id
        );
        if (found) {
          updatedLines = mainStates.lines.filter(
            (item) => item.id !== selectedObject.id
          );
          mainStates.setLines((prev) => {
            const filtered = prev.filter(
              (item) => item.id !== selectedObject.id
            );
            if (filtered.length < prev.length) {
              mainStates.setRemovedLines((prev) => [
                ...prev,
                selectedObject.id,
              ]);
            }
            return filtered;
          });
          return true;
        }
        return false;
      })();

    // If not found in main canvas, try to delete from offcanvas states
    if (!deletedFromMain) {
      findAndRemoveFromState(
        offCanvasStates.offCanvasRectangles,
        offCanvasStates.setOffCanvasRectangles,
        offCanvasStates.setRemovedOffCanvasRectangles,
        "offCanvasRectangles",
        selectedObject
      ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasCircles,
          offCanvasStates.setOffCanvasCircles,
          offCanvasStates.setRemovedOffCanvasCircles,
          "offCanvasCircles",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasArrows,
          offCanvasStates.setOffCanvasArrows,
          offCanvasStates.setRemovedOffCanvasArrows,
          "offCanvasArrows",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasDoubleSidedArrows,
          offCanvasStates.setOffCanvasDoubleSidedArrows,
          offCanvasStates.setRemovedOffCanvasDoubleSidedArrows,
          "offCanvasDoubleSidedArrows",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasScribbles,
          offCanvasStates.setOffCanvasScribbles,
          offCanvasStates.setRemovedOffCanvasScribbles,
          "offCanvasScribbles",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasTexts,
          offCanvasStates.setOffCanvasTexts,
          offCanvasStates.setRemovedOffCanvasTexts,
          "offCanvasTexts",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasImages,
          offCanvasStates.setOffCanvasImages,
          offCanvasStates.setRemovedOffCanvasImages,
          "offCanvasImages",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasVideos,
          offCanvasStates.setOffCanvasVideos,
          offCanvasStates.setRemovedOffCanvasVideos,
          "offCanvasVideos",
          selectedObject
        ) ||
        findAndRemoveFromState(
          offCanvasStates.offCanvasLines,
          offCanvasStates.setOffCanvasLines,
          offCanvasStates.setRemovedOffCanvasLines,
          "offCanvasLines",
          selectedObject
        );
    }

    // Clear all selection states
    setSelectedText(null);
    setSelectedTextId(null);
    setSelectedObject(null);
    setSelectedObjects([]);
    transformerRef.current?.nodes([]);

    // CRITICAL: Save to history with the state BEFORE deletion
    // This ensures undo can restore the element that was deleted
    // We save the state as it was BEFORE deletion, not after
    // IMPORTANT: Call saveToHistory IMMEDIATELY (not in setTimeout) to ensure
    // the state is saved before React state updates complete

    // CRITICAL: Verify the override array before passing it
    // Check if the element type being deleted is present in stateBeforeDeletion
    const elementType = selectedObject.type;
    let hasElement = false;
    if (elementType === "Rect" && stateBeforeDeletion.rectangles && stateBeforeDeletion.rectangles.length > 0) {
      hasElement = stateBeforeDeletion.rectangles.some(r => r.id === selectedObject.id);
    } else if (elementType === "Circle" && stateBeforeDeletion.circles && stateBeforeDeletion.circles.length > 0) {
      hasElement = stateBeforeDeletion.circles.some(c => c.id === selectedObject.id);
    } else if (elementType === "Arrow" && stateBeforeDeletion.arrows && stateBeforeDeletion.arrows.length > 0) {
      hasElement = stateBeforeDeletion.arrows.some(a => a.id === selectedObject.id);
    } else if (elementType === "DoubleSidedArrow" && stateBeforeDeletion.doubleSidedArrows && stateBeforeDeletion.doubleSidedArrows.length > 0) {
      hasElement = stateBeforeDeletion.doubleSidedArrows.some(a => a.id === selectedObject.id);
    } else if (elementType === "Line" && stateBeforeDeletion.lines && stateBeforeDeletion.lines.length > 0) {
      hasElement = stateBeforeDeletion.lines.some(l => l.id === selectedObject.id);
    } else if (elementType === "Text" && stateBeforeDeletion.texts && stateBeforeDeletion.texts.length > 0) {
      hasElement = stateBeforeDeletion.texts.some(t => t.id === selectedObject.id);
    } else if (elementType === "Image" && stateBeforeDeletion.images && stateBeforeDeletion.images.length > 0) {
      hasElement = stateBeforeDeletion.images.some(img => img.id === selectedObject.id);
    }

    if (!hasElement) {
      console.error(
        `❌ [DELETE] ERROR: ${elementType} with id ${selectedObject.id} not found in stateBeforeDeletion!`,
        {
          elementType,
          elementId: selectedObject.id,
          stateBeforeDeletion: {
            rectanglesCount: stateBeforeDeletion.rectangles?.length || 0,
            circlesCount: stateBeforeDeletion.circles?.length || 0,
            arrowsCount: stateBeforeDeletion.arrows?.length || 0,
            linesCount: stateBeforeDeletion.lines?.length || 0,
            textsCount: stateBeforeDeletion.texts?.length || 0,
            imagesCount: stateBeforeDeletion.images?.length || 0,
          },
          selectedObject,
        }
      );
    }

    // CRITICAL: Save to history with state AFTER deletion
    // We use the manually filtered arrays (updatedRectangles, updatedCircles, etc.)
    // that represent the state after the element was removed
    // This ensures undo operations work correctly:
    // - Delete A, Add text to B, Undo -> undoes text addition only (keeps A deleted)
    // - Delete A, Add text to B, Undo, Undo -> undoes deletion (restores A)

    // When deleting an image from off-canvas, we must pass the state AFTER deletion for off-canvas.
    // Otherwise history uses closure's offCanvasImages (stale) and undo can restore the wrong set.
    // Convert blob URLs to data URLs so first-undo can load the remaining image (blob URLs don't survive serialization).
    const rawOffCanvasAfterDelete =
      selectedObject.type === "Image" && !deletedFromMain
        ? (offCanvasStates.offCanvasImages || []).filter(
            (img) => img.id !== selectedObject.id
          )
        : null;
    const updatedOffCanvasImagesOverride =
      rawOffCanvasAfterDelete && rawOffCanvasAfterDelete.length > 0
        ? offCanvasImagesForHistory(rawOffCanvasAfterDelete)
        : rawOffCanvasAfterDelete;

    // DEBUG: Log what we're saving

    setTimeout(() => {
      saveToHistory(
        "delete_object",
        updatedImages, // State AFTER deletion
        updatedArrows,
        updatedDoubleSidedArrows,
        updatedLines,
        updatedTexts,
        null, // currentSelectedTextOverride
        null, // currentSelectedTextIdOverride
        updatedRectangles,
        null, // currentOffCanvasRectanglesOverride
        updatedCircles,
        null, // currentOffCanvasCirclesOverride
        null, // currentOffCanvasArrowsOverride
        null, // currentOffCanvasDoubleSidedArrowsOverride
        null, // currentOffCanvasLinesOverride
        null, // currentOffCanvasTextsOverride
        updatedOffCanvasImagesOverride, // state AFTER deletion for off-canvas images
        selectedObject.id, // deletedObjectId
        selectedObject.type // deletedObjectType
      );
    }, 0); // Minimal delay to ensure React state updates are queued
  };
};
